import deepCopy from 'deep-copy';
import deepEqual from 'plain-object-is-equal';

export default class ObjectUtils {

	static includeObjectKeys(object, predicate) {
		const result = {};

		if (Array.isArray(predicate)) {
			for (const key of predicate) {
				const descriptor = Object.getOwnPropertyDescriptor(object, key);
				if (descriptor?.enumerable) {
					Object.defineProperty(result, key, descriptor);
				}
			}
		} else {
			// `Reflect.ownKeys()` is required to retrieve symbol properties
			for (const key of Reflect.ownKeys(object)) {
				const descriptor = Object.getOwnPropertyDescriptor(object, key);
				if (descriptor.enumerable) {
					const value = object[key];
					if (predicate(key, value, object)) {
						Object.defineProperty(result, key, descriptor);
					}
				}
			}
		}

		return result;
	}

	static excludeObjectKeys(object, predicate) {
		if (Array.isArray(predicate)) {
			const set = new Set(predicate);
			return ObjectUtils.includeObjectKeys(object, key => !set.has(key));
		}

		return ObjectUtils.includeObjectKeys(object, (key, value, object) => !predicate(key, value, object));
	}

	static excludeEmpty<T = any>(value: T, ignoreKeys?: (keyof T)[]): Partial<T> {
		return ObjectUtils.excludeObjectKeys(value, (_, value: any) =>
			ignoreKeys ? (!ignoreKeys.includes(_) && !value) : !value);
	}

	static deepCopy<T = any>(value: T): T {
		return deepCopy(value);
	}

	static excludeKeys<T = any>(value: T, keys: (keyof T)[]): Partial<T> {
		return ObjectUtils.excludeObjectKeys(value, key => keys.includes(key));
	}

	static excludeValues<T = any>(value: T, values: (T[keyof T])[]): Partial<T> {
		return ObjectUtils.excludeObjectKeys(value, (_, _value) => values.includes(_value));
	}

	static excludeEmptyKeysFromArray<T = any>(value: T[], keys: (keyof T)[]): T[] {
		return value.filter(item => keys.every(key => item[key]));
	}

	static includeKeys<T = any>(value: T, keys: (keyof T)[]): Partial<T> {
		return ObjectUtils.includeObjectKeys(value, key => keys.includes(key));
	}

	static deepEqual<T = any, A = any>(
		_value: T,
		_equalTo: A,
		excludeKeysList?: string[],
		skipNotHavingProperties?: boolean
	): boolean {
		let value: any = _value || {};
		let equalTo: any = _equalTo || {};

		if (excludeKeysList?.length || skipNotHavingProperties) {
			const excludeKeyCallback = <T = any>(key: keyof T) =>
				excludeKeysList?.includes(key as string)
				|| (skipNotHavingProperties && (
					!value.hasOwnProperty(key)
					|| !equalTo.hasOwnProperty(key)
				));

			value = ObjectUtils.excludeObjectKeys(value, excludeKeyCallback as any) as T;
			equalTo = ObjectUtils.excludeObjectKeys(equalTo, excludeKeyCallback as any) as A;
		}

		return deepEqual(value as any, equalTo as any);
	}

	static assign<T extends Object | null, Y extends Object | null>(
		from: T,
		to: Y
	): T & Y {
		return to ? { ...(from || {}), ...to } : to as any;
	}

}